import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Copy, Library, Search } from 'lucide-react';
import { SchoolApi } from '../../api/client';
import type { ExamBankRow, ExamKind } from '../../api/types';
import { Badge, EmptyState, ErrorPanel, PageHeader, PanelCard, PillTabs, Pager, Segmented, Spinner } from '../../components/ui';
import { gradeCompact } from '../../lib/gradeLabel';
import { KIND_LABEL, myResultsText } from './bankText';

const PAGE_SIZE = 20;
type KindKey = 'all' | ExamKind;
const KINDS: { key: KindKey; label: string }[] = [
  { key: 'all', label: 'All' }, { key: 'exam', label: 'Exams' },
  { key: 'worksheet', label: 'Worksheets' }, { key: 'homework', label: 'Homework' },
];

function BankRow({ e }: { e: ExamBankRow }) {
  const chapters = e.chapters.slice(0, 3).join(', ') + (e.chapters.length > 3 ? ` +${e.chapters.length - 3}` : '');
  return (
    <li>
      <Link to={`/exam-bank/${e.id}`}
        className="block rounded-[14px] border border-line bg-surface p-4 text-ink no-underline transition-colors hover:border-line-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent">
        <div className="flex flex-wrap items-center gap-2">
          <span className="min-w-0 break-words text-[15px] font-semibold">{e.name}</span>
          <Badge tone={e.kind === 'exam' ? 'navy' : 'info'}>{KIND_LABEL[e.kind]}</Badge>
          {e.delivery === 'paper' && <Badge tone="neutral">Paper</Badge>}
        </div>
        <p className="mt-1 text-sm text-muted">
          {[e.grade ? gradeCompact(e.grade.name) : null, e.subject?.name, `${e.question_count} question${e.question_count === 1 ? '' : 's'}`]
            .filter(Boolean).join(' · ')}
        </p>
        {chapters && <p className="mt-0.5 text-xs text-muted-2">Chapters: {chapters}</p>}
        <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted">
          <span>By {e.creator.name} · {new Date(e.created).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
          <span className="inline-flex items-center gap-1"><Copy size={12} aria-hidden />Copied {e.copies_count} time{e.copies_count === 1 ? '' : 's'}</span>
          <span className={e.my_results.took > 0 ? 'font-semibold text-ink' : ''}>{myResultsText(e.my_results)}</span>
        </div>
      </Link>
    </li>
  );
}

/** Teaching → Exam Bank (/exam-bank): every released exam, worksheet and homework of the
 * school, to read and copy into one's own class. Figures count only the viewer's students. */
export default function ExamBankPage() {
  const [grade, setGrade] = useState<number | 'all'>('all');
  const [subject, setSubject] = useState<number | 'all'>('all');
  const [kind, setKind] = useState<KindKey>('all');
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  // Search waits for a pause in typing; any filter change starts again at page 1.
  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchText.trim()), 300);
    return () => window.clearTimeout(t);
  }, [searchText]);
  useEffect(() => { setPage(1); }, [grade, subject, kind, search]);

  const params = {
    ...(grade !== 'all' ? { grade } : {}), ...(subject !== 'all' ? { subject } : {}),
    ...(kind !== 'all' ? { kind } : {}), ...(search ? { search } : {}), page, page_size: PAGE_SIZE,
  };
  const q = useQuery({ queryKey: ['examBank', params], queryFn: () => SchoolApi.examBank(params), placeholderData: keepPreviousData });
  const facets = q.data?.facets;
  const pageCount = q.data ? Math.max(1, Math.ceil(q.data.count / PAGE_SIZE)) : 1;
  const filtered = grade !== 'all' || subject !== 'all' || kind !== 'all' || !!search;

  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader
        eyebrow="Teaching · Exam Bank"
        title="Exam Bank"
        subtitle="Released exams, worksheets and homework from across your school. Open one to read it, or copy it into your own class."
      />

      <PanelCard size="filter" className="mb-[18px]">
        <div className="space-y-3.5">
          <div className="flex flex-wrap items-center gap-x-[26px] gap-y-3">
            <Segmented label="Type" value={kind} onChange={(k) => setKind(k as KindKey)} options={KINDS} />
            <label className="relative flex min-w-[200px] flex-1 items-center">
              <Search size={16} aria-hidden className="pointer-events-none absolute left-3 text-muted" />
              <input type="search" value={searchText} onChange={(ev) => setSearchText(ev.target.value)}
                placeholder="Search by name" aria-label="Search the exam bank"
                className="h-11 w-full rounded-[12px] border border-line-strong bg-surface pl-9 pr-3 text-sm text-ink placeholder:text-muted focus:border-slate focus:outline-none" />
            </label>
          </div>
          {facets && facets.grades.length > 0 && (
            <div className="flex items-start gap-3.5">
              <span className="pt-[11px] font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">Grade</span>
              <div className="min-w-0 flex-1">
                <PillTabs scroll dense label="Grade" value={String(grade)}
                  onChange={(k) => { setGrade(k === 'all' ? 'all' : Number(k)); setSubject('all'); }}
                  options={[{ key: 'all', label: 'All grades' }, ...facets.grades.map((g) => ({ key: String(g.id), label: gradeCompact(g.name) }))]} />
              </div>
            </div>
          )}
          {facets && facets.subjects.length > 0 && (
            <div className="flex items-start gap-3.5">
              <span className="pt-[11px] font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">Subject</span>
              <div className="min-w-0 flex-1">
                <PillTabs scroll dense label="Subject" value={String(subject)}
                  onChange={(k) => setSubject(k === 'all' ? 'all' : Number(k))}
                  options={[{ key: 'all', label: 'All subjects' }, ...facets.subjects.map((s) => ({ key: String(s.id), label: s.name }))]} />
              </div>
            </div>
          )}
        </div>
      </PanelCard>

      {q.isPending ? (
        <div className="flex justify-center py-16"><Spinner size="md" /></div>
      ) : q.isError ? (
        <ErrorPanel title="The exam bank could not be loaded" body="Try again in a moment." onRetry={() => q.refetch()} />
      ) : q.data.results.length === 0 ? (
        <PanelCard>
          <EmptyState plainTitle icon={<Library size={20} strokeWidth={1.8} aria-hidden />}
            title={filtered ? 'Nothing matches these filters' : 'The exam bank is empty for now'}
            body={filtered ? 'Try another grade, subject or type.'
              : 'Exams, worksheets and homework appear here once they are published and their results are released.'} />
        </PanelCard>
      ) : (
        <>
          <p className="mb-2.5 text-sm text-muted" aria-live="polite">
            {q.data.count} item{q.data.count === 1 ? '' : 's'}
          </p>
          <ul aria-label="Exam bank" className="space-y-2.5">
            {q.data.results.map((e) => <BankRow key={e.id} e={e} />)}
          </ul>
          <Pager className="mt-4 justify-center" page={page} pageCount={pageCount} onPage={setPage} label="Exam bank pages" />
        </>
      )}
    </div>
  );
}
